/** Browsing and creating folders under a root on the PRISM server or a base folder on a storage agent. */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Folder, FolderPlus, Home } from 'lucide-react';
import type { MountListing, StorageAgentDTO, StoragePurpose } from '@prism/contracts';
import { api, errorDetail } from '../api/client';
import { bytes } from '../lib/format';
import { ErrorBanner } from './ErrorBanner';
import { toast } from './Toasts';

/** Where a folder picker browses: a root on the PRISM server, or a base folder on a storage agent. */
export type FolderSource = { type: 'server'; root: 'storage' | 'watch' | 'edit' } | { type: 'agent'; agentId: string; base: 'storage' | 'watch' | 'export' | 'edit' };

/** Where a new agent folder goes: export folders under the agent's export root when it has one, else under its storage root. */
export const agentBaseFor = (agent: StorageAgentDTO | undefined, purpose: StoragePurpose): 'storage' | 'export' | 'edit' =>
  purpose === 'edit' ? 'edit' : purpose === 'export' && agent && 'bases' in agent.capabilities && agent.capabilities.bases?.export?.configured ? 'export' : 'storage';

export const agentRootLabel = (base: 'storage' | 'watch' | 'export' | 'edit') => (base === 'storage' ? 'storage root' : base === 'watch' ? 'watch root' : base === 'edit' ? 'edit root' : 'export root');

const listingUrl = (source: FolderSource, path: string) => source.type === 'server'
  ? `/api/v1/storage/mounts?root=${source.root}&path=${encodeURIComponent(path)}`
  : `/api/v1/agents/${source.agentId}/folders?base=${source.base}&path=${encodeURIComponent(path)}`;

export function useMounts(path: string, enabled = true, source: FolderSource = { type: 'server', root: 'watch' }) {
  return useQuery({ queryKey: ['mounts', source, path], queryFn: () => api<MountListing>(listingUrl(source, path)), enabled, retry: false });
}

/** Browse folders under a server root or on a storage agent, create one, and pick it. */
export function FolderPicker({ path, onPath, source = { type: 'server', root: 'watch' } }: { path: string; onPath: (p: string) => void; source?: FolderSource }) {
  const q = useMounts(path, true, source);
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');
  const crumbs = path ? path.split('/') : [];
  const data = q.data;

  async function create() {
    const name = newName.trim();
    if (!name) return;
    const full = path ? `${path}/${name}` : name;
    try {
      const res = source.type === 'server'
        ? await api<MountListing>('/api/v1/storage/mounts/folders', { method: 'POST', json: { path: full, root: source.root } })
        : await api<MountListing>(`/api/v1/agents/${source.agentId}/folders`, { method: 'POST', json: { path: full, base: source.base } });
      setNewName('');
      await qc.invalidateQueries({ queryKey: ['mounts'] });
      onPath(res.path);
    } catch (err) {
      toast(errorDetail(err), 'error');
    }
  }

  if (q.error) return <ErrorBanner>{errorDetail(q.error)}</ErrorBanner>;
  if (data && !data.available && !data.root) {
    const env = source.type === 'server' && source.root === 'storage' ? { native: 'PRISM_STORAGE_ROOT', compose: 'PRISM_HOST_STORAGE' }
      : source.type === 'server' && source.root === 'edit' ? { native: 'PRISM_EDIT_ROOT', compose: 'PRISM_EDIT_PATH' } : { native: 'PRISM_MOUNTS_ROOT', compose: 'PRISM_HOST_MOUNTS' };
    return <div className="banner warning"><div className="banner-body"><strong>No server folders are configured</strong>
      <span>Set <span className="mono">{env.native}</span> for the API (natively) or <span className="mono">{env.compose}</span> in <span className="mono">.env</span> (Docker Compose), then restart PRISM.</span></div></div>;
  }
  const rootLabel = source.type === 'agent' ? agentRootLabel(source.base) : (data?.root?.split('/').pop() || (source.root === 'storage' ? 'storage' : source.root === 'edit' ? 'edit' : 'mounts'));
  return (
    <div className="folder-picker" data-testid="folder-picker">
      <div className="crumbs">
        <button className="btn ghost sm" onClick={() => onPath('')} title={data?.root ?? ''}><Home size={13} /> {rootLabel}</button>
        {crumbs.map((c, i) => (
          <span key={i} style={{ display: 'contents' }}><ChevronRight size={12} className="faint" />
            <button className="btn ghost sm" onClick={() => onPath(crumbs.slice(0, i + 1).join('/'))}>{c}</button></span>
        ))}
      </div>
      <div className="folder-list">
        {q.isLoading ? <div className="skeleton" style={{ height: 80 }} /> : data?.error ? <ErrorBanner>{data.error}</ErrorBanner> : (
          <>
            {data?.folders.length === 0 && <div className="faint" style={{ padding: 10 }}>No subfolders{data.files ? ` · ${data.files} file${data.files === 1 ? '' : 's'}` : ''}</div>}
            {data?.folders.map((f) => (
              <button key={f.path} className="folder-row" onClick={() => onPath(f.path)} data-testid="folder-row">
                <Folder size={14} /> <span>{f.name}</span>{f.location && <span className="pill" style={{ marginLeft: 'auto' }}>{f.location}</span>}
              </button>
            ))}
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input className="input" placeholder="New folder name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void create(); } }} aria-label="New folder name" />
        <button className="btn sm" onClick={() => void create()} disabled={!newName.trim()}><FolderPlus size={13} /> Create</button>
      </div>
      {data && (
        <div className="faint" style={{ fontSize: 12 }}>
          Selected: <span className="mono">{source.type === 'agent' ? `${rootLabel}/` : `${data.root}/`}{path}</span>
          {data.capacity && ` · ${bytes(data.capacity.freeBytes)} free`}{!data.writable && ' · not writable'}
          {data.location && ` · already used by “${data.location}”`}
        </div>
      )}
    </div>
  );
}
